import { useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Confirm.module.css';

import { useAppSelector } from '../../../hooks/useRedux';
import useModal from '../../../hooks/useModal';
import useConfirm from '../../../hooks/useConfirm';

import { selectMenu } from '../../../features/infoSlice';

import { Modal } from '../';
import { IModal } from '../../../types/modal';
import {
  BUTTON_TEXT,
  CANCEL,
  BODY_TEXT,
  OK,
  KEEP_ALL,
  REPLACE,
  CONFIRM,
  CALCULATING,
  CONVERTING_PAGE,
  COUNT_SUFFIX,
} from '../../../constants';
import { selectStorage } from '../../../features/storageSlice';
import { formatBytes } from '../../../utils/util';

export default function Confirm({ id, type, data }: IModal) {
  const cx = classNames.bind(styles);
  const menu = useAppSelector(selectMenu);
  const storage = useAppSelector(selectStorage);

  const { toggleModal } = useModal();
  const { handleConfirm, handleCancel } = useConfirm(id, data);
  const [willReplace, setWillReplace] = useState(1);

  const hasOneButton =
    id === 'confirmFileExt' ||
    id === 'confirmFileSize' ||
    id === 'confirmBuyStorage' ||
    id === 'pdfConverting' ||
    id === 'membership-change-not-allowed' ||
    id === 'cancel-change-membership' ||
    id === 'basic-membership-required' ||
    id === 'current-membership-required';
  const isYellow = id === 'cancel-change-membership';
  const buttonText = useMemo(() => BUTTON_TEXT[id], [id]);
  const okButtonText = buttonText?.[0] ?? OK;
  const cancelButtonText = buttonText?.[1] ?? CANCEL;
  const bodyTextKey =
    id === 'confirmDeleteFile' && menu === 'mydrive'
      ? 'confirmDeleteDriveStorage'
      : id === 'confirmRemoveTableRow' &&
        `${id}_${data.type}${data.status ? '_' + data.status : ''}`;
  const bodyText = useMemo(
    () =>
      (data?.length > 1 ? `${data?.length}${COUNT_SUFFIX}` : '') +
      BODY_TEXT[bodyTextKey || id]
        .replace('{file}', formatBytes(storage.file))
        .replace('{video}', formatBytes(storage.video)) +
      (data?.addedText ?? ''),
    [id]
  );

  const clickConfirm = () => {
    const data: any = {
      confirmFileReplace: { overwriteYn: willReplace },
    };
    toggleModal({ id, type });
    handleConfirm(data[id]);
  };

  const clickCancel = () => {
    toggleModal({ id, type });
    handleCancel && handleCancel();
  };

  const changeReplaceYn = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setWillReplace(+target.value);
  };

  function Child() {
    switch (id) {
      case 'confirmFileReplace':
        return (
          <div className={cx('option_wrap')}>
            <div className={cx('row')}>
              <div className={cx('radio_wrap')}>
                <label>
                  <input
                    type="radio"
                    name="upload-file-replace"
                    className={cx('blind')}
                    defaultChecked={!!willReplace}
                    value={1}
                    onChange={changeReplaceYn}
                  />
                  <span className={cx('rlabel')}>{REPLACE}</span>
                </label>
              </div>
            </div>
            <div className={cx('row')}>
              <div className={cx('radio_wrap')}>
                <label>
                  <input
                    type="radio"
                    name="upload-file-replace"
                    className={cx('blind')}
                    defaultChecked={!willReplace}
                    value={0}
                    onChange={changeReplaceYn}
                  />
                  <span className={cx('rlabel')}>{KEEP_ALL}</span>
                </label>
              </div>
            </div>
          </div>
        );

      case 'pdfConverting':
        if (data.status === 'pending') {
          return (
            <div className={cx('loading--container')}>
              <div className={cx('loading')}>
                <div className={cx('items')}>{CONVERTING_PAGE}</div>
              </div>
            </div>
          );
        } else {
          const percent = Math.floor((data.current / data.total) * 100);
          return (
            <div className={cx('progress_bar')}>
              <div className={cx('progress', 'rail')}></div>
              {data.current ? (
                <>
                  <div
                    className={cx('progress', 'current')}
                    style={{ width: `${percent}%` }}
                  ></div>
                  <span>
                    {data.current} / {data.total} ({percent}%)
                  </span>
                </>
              ) : (
                <span className={cx('dull')}>{CALCULATING}...</span>
              )}
            </div>
          );
        }

      default:
        return null;
    }
  }

  return (
    <Modal id={id} type={type}>
      <p className={cx('txt')}>
        <span dangerouslySetInnerHTML={{ __html: bodyText }}></span>
      </p>
      <Child />
      <div className={cx('btn_wrap')}>
        {data?.status !== 'pending' && (
          <button
            type="button"
            className={cx('btn', 'b_t', { dull: !isYellow })}
            onClick={clickCancel}
          >
            <span>
              {hasOneButton && id !== 'pdfConverting'
                ? CONFIRM
                : cancelButtonText}
            </span>
          </button>
        )}
        {!hasOneButton && (
          <button
            type="button"
            className={cx('btn', 'b_t', {
              point: id === 'confirmEmptyTrash' || id === 'confirmDelCompletly',
            })}
            onClick={clickConfirm}
          >
            <span>{okButtonText}</span>
          </button>
        )}
      </div>
    </Modal>
  );
}
